import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Modal, Steps, Typography } from 'antd';
import { useAuth } from '@contexts/auth';
import { PalletService, ToastMessage } from '@utility/index';
import {
  DEFAULT_LABEL_FORM_VALUES,
  InboundPalletLabelOptionsForm,
  type LabelFormValues,
} from '@page-components/shipments/inbound/detail/InboundPalletLabelOptionsForm';

const { Text } = Typography;

type ModalStep = 'labels' | 'done';

type BulkGeneratePalletLabelsModalProps = {
  open: boolean;
  onClose: () => void;
  warehouseId: string;
  palletNumbers: string[];
  onCompleted?: () => void;
};

const STEP_ITEMS = [{ title: 'Label options' }, { title: 'Done' }];

export function BulkGeneratePalletLabelsModal({
  open,
  onClose,
  warehouseId,
  palletNumbers,
  onCompleted,
}: BulkGeneratePalletLabelsModalProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<ModalStep>('labels');
  const [loading, setLoading] = useState(false);
  const [labelForm] = Form.useForm<LabelFormValues>();

  const resetState = useCallback(() => {
    setStep('labels');
    labelForm.resetFields();
    labelForm.setFieldsValue(DEFAULT_LABEL_FORM_VALUES);
  }, [labelForm]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  useEffect(() => {
    if (open && step === 'labels') {
      labelForm.setFieldsValue(DEFAULT_LABEL_FORM_VALUES);
    }
  }, [open, step, labelForm]);

  const stepIndex = step === 'labels' ? 0 : 1;

  const handleClose = () => {
    if (loading) return;
    onCompleted?.();
    onClose();
  };

  const handleGenerateLabels = async (): Promise<boolean> => {
    if (!palletNumbers.length) {
      ToastMessage.error('Select at least one pallet.');
      return false;
    }

    try {
      const values = await labelForm.validateFields();
      setLoading(true);

      await PalletService.generatePalletLabels({
        warehouseId,
        palletNumbers,
        labelSize: values.labelSize,
        pageSize: values.pageSize,
        labelFormat: values.labelFormat,
      });

      setStep('done');
      return false;
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return false;
      }
      ToastMessage.apiError(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleOk = async (): Promise<void | boolean> => {
    if (step === 'done') {
      handleClose();
      return;
    }
    return handleGenerateLabels();
  };

  const modalTitle = useMemo(() => {
    if (step === 'labels') return 'Bulk generate labels';
    return 'Labels requested';
  }, [step]);

  return (
    <Modal
      title={modalTitle}
      open={open}
      centered
      onCancel={handleClose}
      destroyOnHidden
      confirmLoading={loading}
      okText={step === 'labels' ? 'Generate labels' : 'Close'}
      cancelText={step === 'done' ? undefined : 'Cancel'}
      cancelButtonProps={step === 'done' ? { style: { display: 'none' } } : undefined}
      onOk={handleOk}
    >
      <Steps
        current={stepIndex}
        items={STEP_ITEMS}
        size="small"
        style={{ marginBottom: 24 }}
      />

      {step === 'labels' && (
        <>
          <Text
            type="secondary"
            style={{ fontSize: 12, display: 'block', marginBottom: 16 }}
          >
            Generating labels for {palletNumbers.length} pallet
            {palletNumbers.length === 1 ? '' : 's'}
          </Text>
          <InboundPalletLabelOptionsForm form={labelForm} />
        </>
      )}

      {step === 'done' && (
        <Text>
          Your labels are being prepared and will be sent to{' '}
          <Text strong>{user?.email ?? 'your email'}</Text> when ready.
        </Text>
      )}
    </Modal>
  );
}
